import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  LearningDevelopmentCatalogSlug,
  LearningDevelopmentEntry,
  LearningDevelopmentOption,
  LearningDevelopmentRequest,
} from './learningDevelopmentTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const base = (catalog: LearningDevelopmentCatalogSlug) => `/api/learning-development/${catalog}`;

/**
 * The four L&D vocabularies, addressed by slug.
 *
 * **One client over four resources**, matching the one controller behind them: the catalog is a
 * route segment, so every call takes it first. That is what lets one index page and one profile
 * page serve all four.
 */
export const learningDevelopmentApi = {
  list: (catalog: LearningDevelopmentCatalogSlug, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<LearningDevelopmentEntry>>(`${base(catalog)}${toQueryString(query)}`, { signal }),

  get: (catalog: LearningDevelopmentCatalogSlug, id: string, signal?: AbortSignal) =>
    api<LearningDevelopmentEntry>(`${base(catalog)}/${id}`, { signal }),

  /**
   * The ACTIVE rows, in the vocabulary's own order.
   *
   * What the Training/L&D form's pickers are built from. Deleted rows are left out — a picker
   * asks what can be chosen now; a record already filed under a retired row keeps it, and the
   * panel still states it.
   */
  options: (catalog: LearningDevelopmentCatalogSlug, signal?: AbortSignal) =>
    api<LearningDevelopmentOption[]>(`${base(catalog)}/options`, { signal }),

  /**
   * Adds one row.
   *
   * **Idempotent on the name**, like the eligibility and family-relation creates: all four of
   * these are added in place from the Training/L&D form's own pickers, so a reader who types one
   * that already exists wanted that row rather than an error about it. The cost, stated rather
   * than hidden: this catalog's own create modal no longer reports a duplicate name — it returns
   * the row holding it. An UPDATE still refuses one.
   */
  create: (catalog: LearningDevelopmentCatalogSlug, body: LearningDevelopmentRequest) =>
    api<LearningDevelopmentEntry>(base(catalog), { method: 'POST', body }),

  update: (catalog: LearningDevelopmentCatalogSlug, id: string, body: LearningDevelopmentRequest) =>
    api<LearningDevelopmentEntry>(`${base(catalog)}/${id}`, { method: 'PUT', body }),

  /** Soft delete. L&D records filed under it keep resolving its name. */
  remove: (catalog: LearningDevelopmentCatalogSlug, id: string) =>
    api<void>(`${base(catalog)}/${id}`, { method: 'DELETE' }),

  restore: (catalog: LearningDevelopmentCatalogSlug, id: string) =>
    api<void>(`${base(catalog)}/${id}/restore`, { method: 'POST' }),

  /** Soft-deletes every row of ONE vocabulary; resolves with how many. */
  clear: (catalog: LearningDevelopmentCatalogSlug) =>
    api<number>(`${base(catalog)}/clear`, { method: 'POST' }),
};
